import React, { useState } from "react";
import { PhoneCall, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useVoip } from "../voip/VoipContext";
import {
  ContactSelectionModal,
  type ContactPhoneOption,
} from "../voip/ContactSelectionModal";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { useCRM } from "../context/CRMContext";
import { appendDealHistoryDirect } from "../deals/useDealHistory";
import { cn } from "@/lib/utils";

interface ActivityCallButtonProps {
  dealId?: string | number | null;
  dealName?: string;
  taskId?: string | number | null;
  contactId?: string | number | null;
  companyId?: string | number | null;
  onCallInitiated?: (info: {
    phone: string;
    contactName: string;
    timestamp: Date;
    callId?: string;
  }) => void;
  variant?: "header" | "inline" | "icon";
  className?: string;
}

export const ActivityCallButton: React.FC<ActivityCallButtonProps> = ({
  dealId,
  dealName,
  taskId,
  contactId,
  companyId,
  onCallInitiated,
  variant = "header",
  className,
}) => {
  const { isVoipEnabled, isSuperadmin } = useCRM();
  const { isConnected, ramal, openLoginModal, makeCall, isCalling } = useVoip();

  const [isFetchingContacts, setIsFetchingContacts] = useState(false);
  const [multipleContactsModalOpen, setMultipleContactsModalOpen] =
    useState(false);
  const [availableContacts, setAvailableContacts] = useState<
    ContactPhoneOption[]
  >([]);

  const hasValidContact = contactId && contactId !== "none";
  const hasValidDeal = dealId && dealId !== "none";

  // Exibe se VoIP estiver habilitado (ou usuário for superadmin) e houver pessoa ou negócio vinculado
  if (
    (!isVoipEnabled && !isSuperadmin) ||
    (!hasValidContact && !hasValidDeal)
  ) {
    return null;
  }

  const extractPhonesFromContactRecord = (
    c: any,
  ): Array<{ phone: string; type: string }> => {
    const list: Array<{ phone: string; type: string }> = [];
    if (!c) return list;

    // 1. Array phone_jsonb
    if (Array.isArray(c.phone_jsonb) && c.phone_jsonb.length > 0) {
      for (const item of c.phone_jsonb) {
        if (!item) continue;
        if (typeof item === "string" && item.trim()) {
          list.push({ phone: item.trim(), type: "Telefone" });
        } else if (typeof item === "object") {
          const num = item.number || item.phone || item.telefone || item.valor;
          if (num && typeof num === "string" && num.trim()) {
            list.push({
              phone: num.trim(),
              type: item.type || item.tipo || "Celular",
            });
          }
        }
      }
    } else if (c.phone_jsonb && typeof c.phone_jsonb === "object") {
      const num = c.phone_jsonb.number || c.phone_jsonb.phone;
      if (num && typeof num === "string" && num.trim()) {
        list.push({
          phone: num.trim(),
          type: c.phone_jsonb.type || "Principal",
        });
      }
    }

    // 2. custom_fields
    if (c.custom_fields && typeof c.custom_fields === "object") {
      const cf = c.custom_fields;
      const fields = ["telefone", "celular", "phone", "whatsapp", "contato"];
      for (const f of fields) {
        if (cf[f] && typeof cf[f] === "string" && cf[f].trim()) {
          list.push({ phone: cf[f].trim(), type: f.toUpperCase() });
        }
      }
    }

    return list;
  };

  const fetchContactsWithPhone = async (): Promise<ContactPhoneOption[]> => {
    const supabase = getSupabaseClient();
    const options: ContactPhoneOption[] = [];
    const addedPhoneNumbers = new Set<string>();

    const addOption = (
      id: string | number,
      name: string,
      phone: string,
      type?: string,
    ) => {
      const cleanPhone = phone.trim();
      if (!cleanPhone || addedPhoneNumbers.has(cleanPhone)) return;
      addedPhoneNumbers.add(cleanPhone);
      options.push({ id, name, phone: cleanPhone, type: type || "Telefone" });
    };

    // 1. PRIORIDADE MÁXIMA: Usa o ID da pessoa na atividade (contactId)
    if (hasValidContact) {
      const parsedContactId = Number(contactId) || contactId;
      const { data: directContact } = await supabase
        .from("contacts")
        .select(
          "id, first_name, last_name, phone_jsonb, custom_fields, company_id",
        )
        .eq("id", parsedContactId)
        .maybeSingle();

      if (directContact) {
        const cName =
          `${directContact.first_name || ""} ${directContact.last_name || ""}`.trim() ||
          "Contato";

        const directPhones = extractPhonesFromContactRecord(directContact);
        for (const p of directPhones) {
          addOption(directContact.id, cName, p.phone, p.type);
        }

        // Se encontrou telefone para a pessoa da atividade, prioriza ela diretamente
        if (options.length > 0) {
          return options;
        }

        // Se a pessoa não tiver telefone próprio cadastrado, mas tiver empresa vinculada
        if (directContact.company_id) {
          const { data: comp } = await supabase
            .from("companies")
            .select("id, name, phone_number")
            .eq("id", directContact.company_id)
            .maybeSingle();

          if (comp?.phone_number && comp.phone_number.trim()) {
            addOption(
              `company-${comp.id}`,
              `${cName} (${comp.name || "Empresa"})`,
              comp.phone_number.trim(),
              "Telefone da Empresa",
            );
            return options;
          }
        }
      }
    }

    // 2. Se não achou telefone pela pessoa da atividade, busca os contatos do negócio (deal)
    if (hasValidDeal) {
      const parsedDealId = Number(dealId) || dealId;
      const { data: deal } = await supabase
        .from("deals")
        .select("id, name, contact_ids, company_id")
        .eq("id", parsedDealId)
        .maybeSingle();

      if (deal) {
        const otherContactIds = (deal.contact_ids || []).filter(
          (id: any) => String(id) !== String(contactId),
        );

        if (otherContactIds.length > 0) {
          const { data: dealContacts } = await supabase
            .from("contacts")
            .select("id, first_name, last_name, phone_jsonb, custom_fields")
            .in("id", otherContactIds);

          if (dealContacts) {
            for (const c of dealContacts) {
              const cName =
                `${c.first_name || ""} ${c.last_name || ""}`.trim() ||
                "Contato";
              const cPhones = extractPhonesFromContactRecord(c);
              for (const p of cPhones) {
                addOption(c.id, cName, p.phone, p.type);
              }
            }
          }
        }

        // 3. Se nenhum contato tiver telefone, tenta o telefone da empresa do negócio
        const effectiveCompanyId = companyId || deal.company_id;
        if (
          options.length === 0 &&
          effectiveCompanyId &&
          effectiveCompanyId !== "none"
        ) {
          const { data: comp } = await supabase
            .from("companies")
            .select("id, name, phone_number")
            .eq("id", effectiveCompanyId)
            .maybeSingle();

          if (comp?.phone_number && comp.phone_number.trim()) {
            addOption(
              `company-${comp.id}`,
              comp.name || "Empresa",
              comp.phone_number.trim(),
              "Telefone da Empresa",
            );
          }
        }
      }
    }

    return options;
  };

  const handleCallSuccess = async (targetContact: ContactPhoneOption) => {
    const now = new Date();
    const formattedDate = format(now, "dd/MM/yyyy 'às' HH:mm");

    // 1. Se tiver negócio vinculado, registra no histórico do negócio
    if (hasValidDeal) {
      try {
        appendDealHistoryDirect(dealId!, {
          type: "activity_created",
          label: `Ligação VoIP para ${targetContact.name}`,
          description: `Chamada VoIP disparada via Atividade para ${targetContact.phone} (${targetContact.type || "Telefone"})`,
          notes: `Chamada iniciada em ${formattedDate} via ramal ${ramal || ""}`,
        });
      } catch {
        // Ignora erro no histórico
      }
    }

    // 2. Notifica o componente pai para atualizar notas e estado da atividade
    onCallInitiated?.({
      phone: targetContact.phone,
      contactName: targetContact.name,
      timestamp: now,
    });

    // 3. Se a atividade já existir no banco (taskId), salva imediatamente na tabela tasks
    if (taskId) {
      try {
        const supabase = getSupabaseClient();
        const { data: currentTask } = await supabase
          .from("tasks")
          .select("id, notes")
          .eq("id", taskId)
          .maybeSingle();

        const logEntry = `📞 [Ligação VoIP] Chamada para ${targetContact.name} (${targetContact.phone}) iniciada em ${formattedDate}.`;
        const updatedNotes = currentTask?.notes
          ? `${currentTask.notes}\n\n${logEntry}`
          : logEntry;

        await supabase
          .from("tasks")
          .update({
            notes: updatedNotes,
            updated_at: now.toISOString(),
          })
          .eq("id", taskId);
      } catch (err) {
        console.warn(
          "[ActivityCallButton] Falha ao atualizar notas no banco:",
          err,
        );
      }
    }
  };

  const triggerCall = async (targetContact: ContactPhoneOption) => {
    const res = await makeCall({
      phone: targetContact.phone,
      contactName: targetContact.name,
      dealId: hasValidDeal ? dealId! : undefined,
      taskId: taskId || undefined,
    });

    if (res.ok) {
      await handleCallSuccess(targetContact);
    }
  };

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (isCalling || isFetchingContacts) return;

    if (!isConnected) {
      openLoginModal();
      return;
    }

    setIsFetchingContacts(true);
    try {
      const contacts = await fetchContactsWithPhone();

      if (contacts.length === 0) {
        toast.info("Esta pessoa/negócio não possui telefone cadastrado.");
        return;
      }

      if (contacts.length === 1) {
        await triggerCall(contacts[0]);
        return;
      }

      setAvailableContacts(contacts);
      setMultipleContactsModalOpen(true);
    } catch (err) {
      console.error("[ActivityCallButton] Erro ao buscar contatos:", err);
      toast.error("Erro ao buscar telefones para chamada");
    } finally {
      setIsFetchingContacts(false);
    }
  };

  const handleSelectContactModal = async (contact: ContactPhoneOption) => {
    setMultipleContactsModalOpen(false);
    await triggerCall(contact);
  };

  const isLoadingAction = isCalling || isFetchingContacts;

  return (
    <>
      {variant === "header" && (
        <Button
          type="button"
          size="sm"
          onClick={handleClick}
          disabled={isLoadingAction}
          className={cn(
            "h-8 px-3 text-xs font-semibold rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5 shadow-sm transition-all duration-200 active:scale-95 cursor-pointer",
            className,
          )}
          title={`Ligar via VoIP para o contato da atividade ${ramal ? `(Ramal ${ramal})` : ""}`}
        >
          {isLoadingAction ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <PhoneCall className="w-3.5 h-3.5" />
          )}
          <span>Ligar via VoIP</span>
        </Button>
      )}

      {variant === "inline" && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleClick}
          disabled={isLoadingAction}
          className={cn(
            "h-7 px-2.5 text-xs font-medium gap-1.5 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 cursor-pointer",
            className,
          )}
          title="Ligar via VoIP para este contato"
        >
          {isLoadingAction ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Phone className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
          )}
          <span>Ligar</span>
        </Button>
      )}

      {variant === "icon" && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={handleClick}
          disabled={isLoadingAction}
          className={cn(
            "h-7 w-7 rounded-full text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 cursor-pointer",
            className,
          )}
          title="Ligar via VoIP"
        >
          {isLoadingAction ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Phone className="w-3.5 h-3.5" />
          )}
        </Button>
      )}

      <ContactSelectionModal
        open={multipleContactsModalOpen}
        onOpenChange={setMultipleContactsModalOpen}
        dealName={dealName}
        contacts={availableContacts}
        onSelectContact={handleSelectContactModal}
        isCalling={isLoadingAction}
      />
    </>
  );
};
